import { fetchMailAttachmentBlobCached } from "@/lib/cache/fetchMailAttachmentBlobCached";
import type { MailAttachmentLocation } from "@/lib/mail/saveMailAttachmentToSuite";
import { suiteApiFetch } from "@/lib/suite/suiteApiFetch";
import type { MailAttachment } from "@/lib/types/mail";

export const AGENDA_ICS_MAX_BYTES = 262144;

export type AgendaIcsCalendar = {
  id: string;
  name: string;
  colorHex: string | null;
};

export type AgendaIcsEventPreview = {
  title: string;
  location: string;
  allDay: boolean;
  whenLabel: string;
};

export type AgendaIcsPreview = {
  already: boolean;
  alreadyCalendarName: string | null;
  storedWhenLabel: string | null;
  event: AgendaIcsEventPreview;
  calendars: AgendaIcsCalendar[];
  defaultCalendarId: string | null;
};

export type AgendaIcsSaved = {
  already: boolean;
  title: string;
  calendarName: string;
  whenLabel: string;
};

type ApiFail = { message?: string };

function failMessage(data: ApiFail, fallback: string): string {
  const message = data.message?.trim();
  return message ? message : fallback;
}

export async function readMailIcsText(
  location: MailAttachmentLocation,
  attachment: MailAttachment
): Promise<{ ok: true; ics: string } | { ok: false; error: string }> {
  if (attachment.size > AGENDA_ICS_MAX_BYTES) {
    return { ok: false, error: "Ce fichier est trop volumineux." };
  }
  try {
    const blob = await fetchMailAttachmentBlobCached({
      accountId: location.accountId,
      folder: location.folder,
      uid: location.messageUid,
      part: attachment.part,
      filename: attachment.filename,
    });
    if (blob.size > AGENDA_ICS_MAX_BYTES) {
      return { ok: false, error: "Ce fichier est trop volumineux." };
    }
    const ics = await blob.text();
    if (ics.length > AGENDA_ICS_MAX_BYTES) {
      return { ok: false, error: "Ce fichier est trop volumineux." };
    }
    return { ok: true, ics };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Lecture du fichier impossible.",
    };
  }
}

export async function previewAgendaIcs(
  ics: string
): Promise<{ ok: true; preview: AgendaIcsPreview } | { ok: false; error: string }> {
  try {
    const res = await suiteApiFetch<AgendaIcsPreview & ApiFail>("agenda-import-ics.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "preview", ics }),
    });
    if (!res.ok || !res.data.event) {
      return { ok: false, error: failMessage(res.data, "Lecture de l’invitation impossible.") };
    }
    return { ok: true, preview: res.data };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Lecture de l’invitation impossible.",
    };
  }
}

export async function saveAgendaIcs(
  ics: string,
  calendarId: string
): Promise<{ ok: true; saved: AgendaIcsSaved } | { ok: false; error: string }> {
  try {
    const res = await suiteApiFetch<AgendaIcsSaved & ApiFail>("agenda-import-ics.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "save", ics, calendarId }),
    });
    if (!res.ok || !res.data.title) {
      return { ok: false, error: failMessage(res.data, "Enregistrement impossible.") };
    }
    return { ok: true, saved: res.data };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Enregistrement impossible.",
    };
  }
}

export function agendaIcsSavedMessage(saved: AgendaIcsSaved): string {
  const title = saved.title || "Rendez-vous";
  const calendar = saved.calendarName || "Agenda";
  if (saved.already) {
    return `« ${title} » est déjà dans l’agenda « ${calendar} ».`;
  }
  return `« ${title} » est dans l’agenda « ${calendar} ».`;
}
